import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  Button,
  Disclosure,
  DisclosureGroupStateContext,
  DisclosurePanel,
} from "react-aria-components";
import CaretRightIcon from "@medoma/components/icons/CaretRightIcon";
import type { IInvoicingCode } from "@medoma/models/invoicing";
import styles from "./InvoicingCodesDisclosure.module.scss";

export const InvoicingCodesDisclosure = ({
  procedureCodesFieldName,
  productCodesFieldName,
  invoicingCodes,
  isExpanded,
  onExpandedChange,
  children,
}: {
  procedureCodesFieldName: string;
  productCodesFieldName: string;
  invoicingCodes: IInvoicingCode[];
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  children: ReactNode;
}) => {
  const { t } = useLingui();
  const { control } = useFormContext();
  const selectedProcedureCodeIds = useWatch({
    control,
    name: procedureCodesFieldName,
    defaultValue: [],
  }) as string[];
  const selectedProductCodeIds = useWatch({
    control,
    name: productCodesFieldName,
    defaultValue: [],
  }) as string[];
  const codesById = new Map(invoicingCodes.map((code) => [code.id, code]));
  const resolveCodes = (ids: string[]) =>
    ids
      .map((id) => codesById.get(id))
      .filter((code): code is IInvoicingCode => code !== undefined);
  const selectedProcedureCodes = resolveCodes(selectedProcedureCodeIds);
  const selectedProductCodes = resolveCodes(selectedProductCodeIds);
  const selectedEntries = [
    ...selectedProcedureCodes.map((code) => ({
      label: t`Åtgärd (KVÅ)`,
      code,
    })),
    ...selectedProductCodes.map((code) => ({
      label: t`Produkt`,
      code,
    })),
  ];

  return (
    // Detach from any ancestor <DisclosureGroup> (e.g. the activities group in
    // GroupedRetroForm) so expanding this one doesn't collapse its parent.
    <DisclosureGroupStateContext.Provider value={null}>
      <Disclosure
        isExpanded={isExpanded}
        onExpandedChange={onExpandedChange}
        className={styles.disclosure}
      >
        <Button slot="trigger" className={styles.trigger}>
          <CaretRightIcon />
          <Trans>Faktureringskoder</Trans>
        </Button>
        {selectedEntries.length > 0 ? (
          <ul className={styles.selectedCodes}>
            {selectedEntries.map(({ label, code }) => (
              <li key={code.id}>{`${label}: ${code.code} - ${code.name}`}</li>
            ))}
          </ul>
        ) : null}
        <DisclosurePanel>{children}</DisclosurePanel>
      </Disclosure>
    </DisclosureGroupStateContext.Provider>
  );
};
